import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, photoUrl } from "@/lib/api";
import { Plus, Trash2, Save, X, Edit3, Upload, Loader2, Images } from "lucide-react";
import { SIZES } from "@/lib/pricing";
import AddonsManager from "@/components/AddonsManager";
import ServiceAreaSettings from "@/components/ServiceAreaSettings";
import SmsStatus from "@/components/SmsStatus";

const EMPTY = {
  name: "",
  slug: "",
  description: "",
  price: 0,
  prices: { small: 0, sedan: 0, suv: 0, large: 0 },
  quote_only: false,
  prerequisite: "",
  duration_minutes: 60,
  features: [],
  image_url: "",
  active: true,
};

export default function AdminServices() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", "service");
      const { data } = await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((e) => ({ ...e, image_url: photoUrl(data.id) }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const load = async () => {
    const { data } = await api.get("/services", { params: { include_inactive: true } });
    setList(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setErr("");
    try {
      const prices = Object.fromEntries(
        SIZES.map((z) => [z.v, Number(editing.prices?.[z.v] ?? editing.price ?? 0)])
      );
      const payload = { ...editing, prices, price: Math.min(...Object.values(prices)), duration_minutes: Number(editing.duration_minutes) };
      if (editing.id) {
        await api.patch(`/services/${editing.id}`, payload);
      } else {
        await api.post("/services", payload);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await api.delete(`/services/${id}`);
    await load();
  };

  return (
    <div data-testid="admin-services">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">
            Services
          </div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-extrabold uppercase text-slate-100">
            Manage packages
          </h1>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="admin-service-add-new-button"
          className="cj-btn-primary px-5 py-3 rounded-full inline-flex items-center gap-2"
        >
          <Plus className="h-4 w-4" /> New Service
        </button>
      </div>

      <div className="mt-6 grid gap-3">
        {list.map((s) => (
          <div
            key={s.id}
            className="cj-card p-5 flex flex-wrap items-center gap-4 justify-between"
            data-testid={`admin-service-row-${s.slug}`}
          >
            <div className="min-w-0 flex-1">
              <div className="font-display uppercase font-extrabold text-lg text-slate-100">
                {s.name}{" "}
                {!s.active && (
                  <span className="cj-pill bg-white/5 text-slate-400 border border-white/10 ml-2">
                    Inactive
                  </span>
                )}
              </div>
              <div className="font-mono text-xs text-slate-500 mt-1">
                /{s.slug} · {s.duration_minutes}m · {s.features?.length || 0} features
                {s.quote_only && <span className="cj-pill bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 ml-2">Quote only</span>}
                {s.prerequisite && <span className="cj-pill bg-amber-500/10 text-amber-300 border border-amber-500/30 ml-2">Has requirement</span>}
              </div>
              <div className="font-mono text-[10px] text-slate-400 mt-1" data-testid={`admin-service-prices-${s.slug}`}>
                {SIZES.map((z) => `${z.l} $${(s.prices?.[z.v] ?? s.price).toFixed(0)}`).join(" · ")}
              </div>
            </div>
            <div className="font-display font-extrabold text-xl text-[#ff6b00]">
              from ${s.price.toFixed(0)}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setEditing(s)}
                data-testid={`admin-service-edit-${s.slug}`}
                className="cj-btn-ghost px-3 py-2 rounded-lg text-xs inline-flex items-center gap-1"
              >
                <Edit3 className="h-3.5 w-3.5" /> Edit
              </button>
              <button
                onClick={() => del(s.id)}
                data-testid={`admin-service-delete-${s.slug}`}
                className="px-3 py-2 rounded-lg text-xs text-red-400 border border-red-500/30 hover:bg-red-500/10 inline-flex items-center gap-1"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      <AddonsManager />
      <ServiceAreaSettings />
      <SmsStatus />

      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <div className="w-full max-w-xl bg-[#0b0f17] border border-white/10 rounded-2xl p-6" data-testid="admin-service-editor">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#ff6b00]">
                  {editing.id ? "Edit service" : "New service"}
                </div>
                <div className="font-display uppercase font-extrabold text-xl text-slate-100 mt-1">
                  Service details
                </div>
              </div>
              <button onClick={() => setEditing(null)} className="h-9 w-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center">
                <X className="h-4 w-4 text-slate-200" />
              </button>
            </div>
            <div className="mt-5 grid gap-4">
              <F label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="svc-name" />
              <F label="Slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} testid="svc-slug" />
              <F
                label="Description"
                value={editing.description}
                onChange={(v) => setEditing({ ...editing, description: v })}
                multiline
                testid="svc-desc"
              />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {SIZES.map((z) => (
                  <F
                    key={z.v}
                    label={`${z.l} ($)`}
                    value={editing.prices?.[z.v] ?? editing.price ?? 0}
                    onChange={(v) => setEditing({ ...editing, prices: { ...(editing.prices || {}), [z.v]: v } })}
                    type="number"
                    testid={`svc-price-${z.v}`}
                  />
                ))}
              </div>
              <F
                label="Duration (min)"
                value={editing.duration_minutes}
                onChange={(v) => setEditing({ ...editing, duration_minutes: v })}
                type="number"
                testid="svc-duration"
              />
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={!!editing.quote_only}
                  onChange={(e) => setEditing({ ...editing, quote_only: e.target.checked })}
                  data-testid="svc-quote-only-checkbox"
                />
                Quote only (prices shown as "from", final price confirmed by CJ)
              </label>
              <F
                label="Booking requirement (optional — customer must tick to confirm)"
                value={editing.prerequisite || ""}
                onChange={(v) => setEditing({ ...editing, prerequisite: v })}
                multiline
                testid="svc-prerequisite"
              />
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-1.5">
                  Service photo
                </div>
                <div className="flex items-start gap-4">
                  <div className="h-24 w-32 rounded-xl overflow-hidden border border-white/10 bg-[#0f1522] flex items-center justify-center shrink-0">
                    {editing.image_url ? (
                      <img src={editing.image_url} alt="Service" className="w-full h-full object-cover" data-testid="svc-image-preview" />
                    ) : (
                      <Images className="h-6 w-6 text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        data-testid="svc-image-upload-button"
                        className="cj-btn-ghost px-4 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50"
                      >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        {uploading ? "Uploading…" : editing.image_url ? "Replace photo" : "Upload photo"}
                      </button>
                      {editing.image_url && (
                        <button
                          type="button"
                          onClick={() => setEditing({ ...editing, image_url: "" })}
                          data-testid="svc-image-remove-button"
                          className="px-4 py-2.5 rounded-full text-sm text-red-400 border border-red-500/30 hover:bg-red-500/10 inline-flex items-center gap-2"
                        >
                          <Trash2 className="h-4 w-4" /> Remove
                        </button>
                      )}
                    </div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      hidden
                      data-testid="svc-image-file-input"
                      onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0])}
                    />
                    <input
                      type="text"
                      value={editing.image_url || ""}
                      onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                      placeholder="…or paste an image URL"
                      data-testid="svc-image"
                      className="mt-2 w-full bg-[#0f1522] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-[#ff6b00]"
                    />
                  </div>
                </div>
              </div>
              <F
                label="Features (one per line)"
                value={(editing.features || []).join("\n")}
                onChange={(v) =>
                  setEditing({ ...editing, features: v.split("\n").map((x) => x.trim()).filter(Boolean) })
                }
                multiline
                testid="svc-features"
              />
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={!!editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  data-testid="svc-active-checkbox"
                />
                Active (bookable by customers)
              </label>
              {err && <div className="text-sm text-red-400 font-mono">{err}</div>}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="cj-btn-ghost px-5 py-2.5 rounded-full">
                Cancel
              </button>
              <button
                onClick={save}
                data-testid="admin-service-save-button"
                className="cj-btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2"
              >
                <Save className="h-4 w-4" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, value, onChange, type = "text", multiline, testid }) {
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-1.5">
        {label}
      </div>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full bg-[#0f1522] border border-white/10 rounded-xl px-4 py-3 text-slate-200 focus:outline-none focus:border-[#ff6b00] min-h-[90px]"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full bg-[#0f1522] border border-white/10 rounded-xl px-4 py-3 text-slate-200 focus:outline-none focus:border-[#ff6b00]"
        />
      )}
    </div>
  );
}
